import { CATEGORY_COLORS, getCategoryLabel, CATEGORIES } from "./CategoryChips";
import { ArrowUpDown, Calendar, Star, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChunkItem {
  id: number;
  category: string;
  title: string;
  importance: number | null;
  createdAt: string | null;
  subcategory: string | null;
  sourceType: string | null;
  content: string;
}

interface ChunkListViewProps {
  chunks: ChunkItem[];
  selectedNodeId: number | null;
  onSelectNode: (id: number) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  isLoading: boolean;
}

const CATEGORY_ORDER: string[] = CATEGORIES.map(c => c.key);

export function ChunkListView({ chunks, selectedNodeId, onSelectNode, sortBy, onSortChange, isLoading }: ChunkListViewProps) {
  const sortedChunks = [...chunks].sort((a, b) => {
    switch (sortBy) {
      case "importance":
        return (b.importance ?? 5) - (a.importance ?? 5);
      case "category": {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        const catDiff = (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        if (catDiff !== 0) return catDiff;
        return (b.importance ?? 5) - (a.importance ?? 5);
      }
      case "alpha":
        return a.title.localeCompare(b.title);
      case "newest":
      default:
        return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="chunk-list-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (chunks.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-testid="chunk-list-empty">
        <p className="text-sm text-gray-500">No knowledge chunks found</p>
      </div>
    );
  }

  const sortOptions = [
    { key: "newest", label: "Newest" },
    { key: "importance", label: "Importance" },
    { key: "category", label: "Category" },
    { key: "alpha", label: "A-Z" },
  ];

  return (
    <div className="space-y-3" data-testid="chunk-list-view">
      <div className="flex items-center gap-2">
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
        {sortOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => onSortChange(opt.key)}
            className={cn(
              "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
              sortBy === opt.key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            )}
            data-testid={`sort-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-auto">{chunks.length} nodes</span>
      </div>

      <div className="space-y-1.5">
        {sortedChunks.map(chunk => {
          const color = CATEGORY_COLORS[chunk.category] || "#6B7280";
          const isSelected = chunk.id === selectedNodeId;
          const dateStr = chunk.createdAt
            ? new Date(chunk.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : "";

          return (
            <button
              key={chunk.id}
              onClick={() => onSelectNode(chunk.id)}
              className={cn(
                "w-full text-left p-3 rounded-lg border transition-all",
                isSelected
                  ? "bg-indigo-50 border-indigo-200 ring-1 ring-indigo-200"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
              )}
              data-testid={`chunk-item-${chunk.id}`}
            >
              <div className="flex items-start gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <h4 className="text-sm font-medium text-gray-900 truncate">{chunk.title}</h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                        <Star className="h-2.5 w-2.5" /> {chunk.importance ?? 5}
                      </span>
                      {dateStr && (
                        <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                          <Calendar className="h-2.5 w-2.5" /> {dateStr}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{chunk.content}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold text-white"
                      style={{ backgroundColor: color }}
                    >
                      {getCategoryLabel(chunk.category)}
                    </span>
                    {chunk.sourceType && (
                      <span className="text-[10px] text-gray-400">{chunk.sourceType}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
